import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  IconMap2,
  IconAxe,
  IconScanEye,
  IconTruckDelivery,
  IconCarCrane,
  IconUsersGroup,
  IconCash,
  IconArrowsExchange,
  IconChartLine,
  IconDeviceMobile,
  IconLogout,
  IconUser,
  IconBuilding,
} from "@tabler/icons-react";

import { AsiLogo } from "./AsiLogo";
import { clearSession, getSession } from "../lib/auth";

const NAV: Array<{ to: string; icon: React.ReactNode; label: string; badge?: string }> = [
  { to: "/estate-land", icon: <IconMap2 size={16} />, label: "Estate Land" },
  { to: "/harvesting", icon: <IconAxe size={16} />, label: "Harvesting" },
  { to: "/smart-grading", icon: <IconScanEye size={16} />, label: "Smart Grading", badge: "AI" },
  { to: "/delivery", icon: <IconTruckDelivery size={16} />, label: "Delivery" },
  { to: "/vehicle", icon: <IconCarCrane size={16} />, label: "Vehicle" },
  { to: "/manpower", icon: <IconUsersGroup size={16} />, label: "Manpower" },
  { to: "/estate-sales", icon: <IconCash size={16} />, label: "Estate Sales" },
  { to: "/subcontract", icon: <IconArrowsExchange size={16} />, label: "Subcontract" },
  { to: "/reports", icon: <IconChartLine size={16} />, label: "Smart Harvesting & AI Grading · Dashboard", badge: "AI" },
  { to: "/mobile", icon: <IconDeviceMobile size={16} />, label: "Mobile view" },
];

export function Layout() {
  const navigate = useNavigate();
  const session = getSession();
  return (
    <div className="min-h-screen bg-surface-1">
      <div className="max-w-[1180px] mx-auto p-2 md:p-4">
        <div className="bg-surface-2 border border-border rounded-xl overflow-hidden">
          {/* Header · dark navy-blue-purple chrome */}
          <header
            className="flex flex-wrap items-center justify-between gap-2 px-3 md:px-4 py-3 border-b border-black text-[#F7F3EA]"
            style={{
              background:
                "radial-gradient(circle at 100% 0%, rgba(60,40,120,0.5) 0%, transparent 55%), linear-gradient(135deg, #0B0721 0%, #1E1848 100%)",
            }}
          >
            <div className="flex items-center gap-3">
              <AsiLogo variant="mark" size={30} />
              <div className="flex flex-col leading-tight">
                <div className="text-[14px] md:text-[15px] font-bold text-[#F7F3EA]">
                  ASI <span className="text-[#E8A733]">Builder</span>
                  <span className="ml-1 font-medium text-[#C9BDF0]">
                    · Smart Harvesting
                  </span>
                </div>
                <div className="text-[10px] text-[#C9BDF0]/80 tracking-wide">
                  AI Automation Agent
                </div>
              </div>
              <span className="hidden sm:inline bg-[#E8A733] text-[#14102E] px-2 py-0.5 rounded text-[11px] font-semibold">
                Pro + Hybrid
              </span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 text-xs text-[#C9BDF0]">
              <span className="hidden sm:flex border border-dashed border-[#C9BDF0]/40 px-2 py-0.5 rounded items-center gap-1">
                <IconBuilding size={13} />{" "}
                {session?.tenant?.name ?? "Demo Tenant (dev mode)"}
              </span>
              <span className="hidden md:flex items-center gap-1">
                <IconUser size={14} />{" "}
                {session
                  ? `${session.user.name} · ${session.user.role}${session.workspace === "field" ? " · 📱" : ""}`
                  : "dev"}
              </span>
              <button
                onClick={() => {
                  clearSession();
                  navigate("/login", { replace: true });
                }}
                title="Sign out"
                className="flex items-center gap-1 rounded border border-[#C9BDF0]/30 px-2 py-0.5 hover:bg-white/10"
              >
                <IconLogout size={13} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </header>

          {/* Phone · horizontal scrolling module bar on top.
              Desktop (md+) · navy-purple sidebar. */}
          <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] min-h-[580px]">
            <nav
              className="flex md:block overflow-x-auto md:overflow-visible border-b md:border-b-0 md:border-r border-black p-2 md:p-3 text-xs text-[#B9AEE4]"
              style={{
                background:
                  "linear-gradient(180deg, #14102E 0%, #0B0721 100%)",
              }}
            >
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex shrink-0 items-center gap-2 px-2.5 py-2 rounded md:mb-0.5 mr-1 md:mr-0 relative whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-[#E8A733] text-[#14102E] font-semibold"
                        : "text-[#C9BDF0] hover:bg-white/5"
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="absolute top-0.5 right-1 bg-[#E8A733] text-[#14102E] text-[9px] px-1 py-px rounded font-bold">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Main content — pages render here */}
            <main className="p-3 md:p-4 min-w-0">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
